import React, { useState } from 'react';
import PropTypes from 'prop-types';
import MatchArrowIcon from 'screens/Integrations/PropertiesMatch/components/FarmList/components/icons/MatchArrowIcon';
import { Switch, Tag } from 'syngenta-digital-cropwise-react-ui-kit';
import ProductDropdown from 'components/ProductDropdown';
import useFormikHandler from 'screens/Integrations/hooks/useFormikHandler';

const ProductListItem = ({
  product,
  matchedProduct = undefined,
  handleSelect
}) => {
  const {
    selectedInvoices,
    excludedProductsIds,
    setExcludedProductsIds
  } = useFormikHandler();

  const idKey = product.retailerId || product.vendorId;

  const [isIgnored, setIsIgnored] = useState(() => {
    if (!excludedProductsIds) return false;
    const products = selectedInvoices?.flatMap(invoice => invoice.products);
    return products.some(
      p =>
        (p.retailerId === idKey || p.vendorId === idKey) &&
        excludedProductsIds?.includes(p.id)
    );
  });

  const handleIgnoreToggle = () => {
    const newIsIgnored = !isIgnored;
    setIsIgnored(newIsIgnored);

    if (!idKey) return;

    const products = selectedInvoices.flatMap(invoice => invoice.products);
    const prevExcludedIds = excludedProductsIds || [];
    if (newIsIgnored) {
      const idsToAdd = products
        .filter(p => p.retailerId === idKey || p.vendorId === idKey)
        .map(p => p.id);
      const updatedIds = new Set([...(prevExcludedIds || []), ...idsToAdd]);
      setExcludedProductsIds(Array.from(updatedIds));
    } else {
      setExcludedProductsIds(
        prevExcludedIds.filter(
          excludedId =>
            !products.some(
              p =>
                (p.retailerId === idKey || p.vendorId === idKey) &&
                p.id === excludedId
            )
        )
      );
    }
  };

  return (
    <div className="justify-self-start pr-15 col-span-w w-full mb-8">
      <div className="grid items-center grid-cols-7">
        <div className="text-sm col-span-3" style={{ position: 'relative' }}>
          <div className="flex flex-row items-center">
            <div className="mr-4 mb-2 flex-none w-22">
              {isIgnored && (
                <Tag
                  key="product-status"
                  color="#edf2f7"
                  data-testid="status-tag"
                  style={{ padding: '0.5rem', lineHeight: '1px' }}
                >
                  IGNORED
                </Tag>
              )}
              {!isIgnored && (
                <Tag
                  key="product-status"
                  color={matchedProduct ? 'green' : 'yellow'}
                  data-testid="status-tag"
                  style={{ padding: '0.5rem', lineHeight: '1px' }}
                >
                  {matchedProduct ? 'MATCHED' : 'UNMATCHED'}
                </Tag>
              )}
            </div>
            <div className="flex flex-col my-2 w-full">
              <span className="text-gray-600">Retailer Product</span>
              <div className="mt-1 px-2 py-1 rounded-md bg-gray-200 border-gray-500 border text-gray-600">{`${product.productName} (${product.totalQuantityUnit})`}</div>
              <div className="my-auto pt-2">
                <Switch
                  size="medium"
                  checked={isIgnored}
                  onChange={handleIgnoreToggle}
                />
                <span className="ml-2">Ignore Product</span>
              </div>
            </div>
          </div>
        </div>
        {!isIgnored && (
          <>
            <div className="flex items-center justify-center h-full col-span-1 mb-2">
              <MatchArrowIcon />
            </div>
            <div className="flex flex-col col-span-3 mb-8">
              <div
                className="text-neutral-600 text-left mb-1 text-sm"
                data-testid="cropzones-heading"
              >
                Cropwise Financials Product
              </div>
              <div className="flex items-center justify-center cropzone-dropdown">
                <ProductDropdown
                  id="cwfProductMatch"
                  value={matchedProduct?.cwfEntityId ?? null}
                  onChange={value => {
                    handleSelect(value);
                  }}
                />
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

ProductListItem.propTypes = {
  product: PropTypes.shape({
    externalId: PropTypes.string,
    productName: PropTypes.string,
    totalQuantityUnit: PropTypes.string,
    retailerId: PropTypes.string,
    vendorId: PropTypes.string
  }).isRequired,
  matchedProduct: PropTypes.shape({
    vendorEntityDescription: PropTypes.string,
    vendorEntityId: PropTypes.string,
    cwfEntityId: PropTypes.string,
    type: PropTypes.string
  }),
  handleSelect: PropTypes.func.isRequired
};

export default ProductListItem;
